import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, {css} from 'styled-components';
import {ImageType} from '../../../../types/Image';
import {getRatio} from '../../../atoms/Image/helpers';
import {Ratio} from '../../../atoms/Image/types';
import {
  ContentBlockCTA,
  ContentBlockCTAProps,
} from '../ContentBlockCTA/ContentBlockCTA';

export interface VideoType {
  url: string;
  description: string;
  fallbackImage: ImageType & {aspectRatio: Ratio};
  aspectRatio: Ratio;
}

export interface ContentBlockVideoProps {
  cta?: ContentBlockCTAProps;
  video: VideoType;
  testId?: string;
  className?: string;
  onClickCTA?: VoidFunction;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  onEndVideoCallback?: VoidFunction;
}

const context = createContext<React.MutableRefObject<HTMLVideoElement | null>>({
  current: null,
});

const useCurrentPlayingVideo = () => useContext(context);

export const ContentBlockVideo: React.FC<ContentBlockVideoProps> = ({
  cta,
  video,
  testId = 'videoTile',
  className,
  onClickCTA,
  videoRef,
  onEndVideoCallback,
}) => {
  useHandleVideoLoop(videoRef, onEndVideoCallback);
  const [showPlayerControls, setShowPlayerControls] = useState(true);
  const {widthRatio, heightRatio} = getRatio(video.aspectRatio);

  const currentPlayingVideoRef = useCurrentPlayingVideo();
  const handleOnPlay = () => {
    // We are using global variable set by useLiveShoppingPlayer() hook because live show provider instance is not accessible
    // Nice solution: would be great to abstract bambuser player away. Idea: migrate from a legacy.
    if (typeof window !== 'undefined') {
      window.LiveShoppingPlayer?.pause();
    }

    if (
      currentPlayingVideoRef.current !== null &&
      currentPlayingVideoRef.current !== videoRef.current
    ) {
      currentPlayingVideoRef.current.muted = true;
    }

    currentPlayingVideoRef.current = videoRef.current;
  };

  return (
    <Wrapper className={className}>
      {cta && cta.text.text !== '' && (
        <ContentBlockCTA
          testId={`${testId}CTA`}
          text={cta.text}
          link={cta.link}
          onClick={onClickCTA}
        />
      )}
      <Container data-testid={`${testId}Container`}>
        <Video
          muted
          onPlay={handleOnPlay}
          ref={videoRef}
          data-testid={testId}
          // loop <- loop is handled in videoEndHandler function
          src={video.url}
          preload={video.url}
          poster={video.fallbackImage.url}
          controls={showPlayerControls}
          aspectRatio={
            `${widthRatio} / ${heightRatio}` as `${number} / ${number}`
          }
          onError={() => {
            setShowPlayerControls(false);
          }}
        />
      </Container>
    </Wrapper>
  );
};

const useHandleVideoLoop = (
  videoRef: React.RefObject<HTMLVideoElement | null>,
  onEndVideoCallback?: VoidFunction,
) => {
  const loopVideo = useRef(true);

  // The next useEffect is handling eventListeners for the video tile on this custom hook, it's taking many lines mostly because of the 2 function handlers
  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    const elRef = videoRef.current;
    const videoEndHandler = () => {
      if (elRef) {
        onEndVideoCallback?.();
        elRef.play();
      }
    };

    const videoClickHandler = () => {
      if (elRef) {
        loopVideo.current = elRef.loop;
        elRef.loop = false;
      }
    };

    const preventSetLoop = () => {
      if (elRef && elRef.loop) {
        elRef.loop = false;
      }
    };

    elRef?.addEventListener('ended', videoEndHandler);
    elRef?.addEventListener('click', videoClickHandler);
    elRef?.addEventListener('timeupdate', preventSetLoop);

    return () => {
      elRef?.removeEventListener('ended', videoEndHandler);
      elRef?.removeEventListener('click', videoClickHandler);
      elRef?.removeEventListener('timeupdate', preventSetLoop);
    };
  }, [videoRef.current]);
};

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const Container = styled.div`
  position: relative;
  display: inline-grid;
  grid-template-columns: auto;
  grid-template-rows: auto;
  grid-column: 1;
  grid-row: 1;
  height: 100%;
  overflow: hidden;
  width: 100%;
`;

const Video = styled.video<{aspectRatio: `${number} / ${number}`}>`
  height: 100%;
  object-fit: cover;
  object-position: 50% 0;
  width: 100%;

  ${({aspectRatio}) =>
    css`
      aspect-ratio: ${aspectRatio};
    `}
`;
